import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  HostListener,
  Injector,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { BaseComponent } from '../../core/base.component';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { HeaderService } from '../../core/services/header.service';
import { Title } from '@angular/platform-browser';
import { NotificationService } from '../../notification/notification.service';
import { AuthService } from '../../core/security/auth.service';
import { RolesEnum } from '../../core/security/roles.enum';
import { CurrentUser, EventsFlowItemModel, EventsFlowModel } from '../../api/models';
import { MatConfirmDialogComponent } from '../../dialogs/mat-confirm/mat-confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { EventsFlowService } from '../services/events-flow.service';
import { EFlowActionPanelEvent } from '../../core/visual/work-flow-editor/action-panel/e-flow-action-panel-event';
import { BehaviorSubject, forkJoin, merge, Observable, of, Subject, Subscription } from 'rxjs';
import {
  EFConnectableSide,
  EFConnectionBehavior,
  EFConnectionType,
  EFMarkerType,
  FCanvasComponent,
  FCreateConnectionEvent,
  FCreateNodeEvent,
  FFlowComponent,
  FReassignConnectionEvent,
  FSelectionChangeEvent,
  FZoomDirective,
  FFlowModule,
  FBackgroundComponent,
  FCirclePatternComponent,
} from '@foblex/flow';
import { EOperationSystem, GuidExtensions, IPoint, IRect, MouseEventExtensions } from '@foblex/core';
import { A, BACKSPACE, DASH, DELETE, NUMPAD_MINUS, NUMPAD_PLUS } from '@angular/cdk/keycodes';
import { catchError, distinctUntilChanged, filter, map, startWith, take } from 'rxjs/operators';
import { IFlowViewModel } from '../../core/visual/work-flow-editor/models/i-flow-view-model';
import { FFlowService } from '../../core/visual/work-flow-editor/f-flow.service';
import { INodeStaticMapItem } from './palette/i-node-static-map-item';
import { hasEventsFlowEventNodeTypes } from './models/events-flow-event-node-types';
import { INodeViewModel } from '../../core/visual/work-flow-editor/models/i-node-view-model';
import { WorkflowNodeComponent } from './node/workflow-node.component';
import { Overlay, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { WorkflowPaletteActionsComponent } from './palette/workflow-palette-actions/workflow-palette-actions.component';
import { ActionsNotes } from './models/action-notes.interface';
import { DirectionType } from './models/direction-type.enum';
import { EventsFlowAlignmentService } from '../services/events-flow-alignment.service';
import { EventsFlowDataFormService } from '../services/events-flow-data-form.service';
import { FlexModule } from '@angular/flex-layout';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { WorkflowPaletteEventsComponent } from './palette/workflow-palette-events/workflow-palette-events.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { WorkflowNodeParametersComponent } from './node-parameters/workflow-node-parameters.component';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';
import { WorkflowActionPanelComponent } from '../../core/visual/work-flow-editor/action-panel/workflow-action-panel.component';

@Component({
  selector: 'app-edit-events-flow',
  templateUrl: './edit.component.html',
  styleUrls: ['./edit.component.scss'],
  standalone: true,
  imports: [
    FlexModule,
    NgIf,
    MatIcon,
    WorkflowPaletteEventsComponent,
    MatProgressSpinner,
    FFlowComponent,
    FFlowModule,
    FBackgroundComponent,
    FCirclePatternComponent,
    FCanvasComponent,
    FZoomDirective,
    NgFor,
    WorkflowNodeComponent,
    WorkflowNodeParametersComponent,
    MatButton,
    MatTooltip,
    Angular2PromiseButtonModule,
    WorkflowActionPanelComponent,
    AsyncPipe,
  ],
})
export class EventsFlowEditComponent extends BaseComponent implements OnInit, OnDestroy {
  @HostBinding('tabindex') tabindex = -1;

  @ViewChild(FFlowComponent, { static: false })
  public fFlowComponent!: FFlowComponent;
  @ViewChild(FCanvasComponent, { static: false })
  public fCanvasComponent!: FCanvasComponent;
  @ViewChild(FZoomDirective, { static: false })
  public fZoomDirective!: FZoomDirective;
  @ViewChildren('fNode') workflowNodes!: QueryList<WorkflowNodeComponent>;

  public submitted: boolean;
  public serverError: boolean;
  public buttonSubmitPromise: Promise<void>;
  public viewModel: IFlowViewModel<any> = { key: 'new', nodes: [], connections: [] };
  public eventsOpened = false;
  public actionsOpened = false;
  public isContentMinimized = false;
  public actionNotes$ = new BehaviorSubject<ActionsNotes>(null);
  public selectedNodes$ = new BehaviorSubject<INodeViewModel<any>[]>([]);

  public readonly eMarkerType = EFMarkerType;
  public readonly eConnectableSide = EFConnectableSide;
  public readonly cBehavior = EFConnectionBehavior.FIXED_CENTER;
  public readonly cType = EFConnectionType.SEGMENT;

  public readonly isDataLoaded$ = new BehaviorSubject<boolean>(false);

  private overlayRef!: OverlayRef;
  private currentUser: CurrentUser;
  private editModel: EventsFlowModel;

  private readonly subscriptions$: Subscription = new Subscription();
  private readonly hasChanges$: Subject<void> = new Subject<void>();
  private readonly fFlowService = new FFlowService<any>(this.eventsFlowDataFormService);

  private get routeKeyChange$(): Observable<boolean> {
    return this.router.events.pipe(
      startWith(new NavigationEnd(0, '', '')),
      filter(x => x instanceof NavigationEnd),
      map(() => this.activatedRoute.snapshot.params['id']),
      distinctUntilChanged(),
      map(() => true),
    );
  }

  $event: KeyboardEvent;

  constructor(
    route: ActivatedRoute,
    headerService: HeaderService,
    titleService: Title,
    authService: AuthService,
    public dialog: MatDialog,
    private eventsFlowService: EventsFlowService,
    private activatedRoute: ActivatedRoute,
    private changeDetectorRef: ChangeDetectorRef,
    private router: Router,
    private notificationService: NotificationService,
    private overlay: Overlay,
    private injector: Injector,
    private eventsFlowAlignmentService: EventsFlowAlignmentService,
    public eventsFlowDataFormService: EventsFlowDataFormService,
  ) {
    super(route, headerService, titleService, authService);
    this.headerService.showBackButton(true);

    this.editRoles = [RolesEnum.AccountRoles_Edit, RolesEnum.AccountRoles_Delete];
    this.deleteRoles = [RolesEnum.AccountRoles_Delete];
  }

  get isNewEvent(): boolean {
    return this.viewModel?.key === 'new';
  }

  get isFormValid(): Boolean {
    return this.eventsFlowDataFormService.isEventsFlowFormValid;
  }

  get hasNodes(): Boolean {
    return !!this.viewModel.nodes?.length;
  }

  public async ngOnInit() {
    super.ngOnInit();

    this.subscriptions$.add(this.subscribeReloadEvents());
    this.loadNotes();

    this.onDeleteProcess = this.onDeleteProcess.bind(this);
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.headerService.showBackButton(false);
    this.subscriptions$.unsubscribe();
  }

  public onCurrentUserChanged(user: CurrentUser): void {
    this.currentUser = user;
  }

  public async onSubmit(): Promise<void> {
    if (!this.isFormValid) {
      return;
    }

    this.serverError = false;
    const reqData = this.prepareFormModel();
    console.log(reqData);

    const isAllNodesConnected = this.fFlowService.isAllNodesConnected(this.viewModel);
    console.log('isAllNodesConnected', isAllNodesConnected);
    if (isAllNodesConnected) {
      this.submitted = true;
      this.buttonSubmitPromise = this.processSubmit();
    }
  }

  public async onDeleteClick(): Promise<void> {
    const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
      // width: '250px',
      data: {
        title: 'Delete Events Flow',
        text: 'Are you sure you want to delete events flow?',
        actionTitle: 'Delete',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(async result => {
        if (result) {
          await this.onDeleteProcess();
        }
      });
  }

  public onLoaded(): void {
    this.fCanvasComponent?.resetScaleAndCenter(false);
  }

  public onCreateNode(event: FCreateNodeEvent): INodeViewModel<any> {
    console.log(event);

    const data = event.data as INodeStaticMapItem;
    let value: any;
    if (hasEventsFlowEventNodeTypes(data.type)) {
      value = {
        description: data.description,
        domain: this.currentUser?.domain,
        drawData: undefined,
        enabled: true,
        eventConditions: undefined,
        eventData: undefined,
        eventType: data.type,
        id: 'new',
        items: [],
        name: data.name,
      };
    } else {
      value = {
        actionName: data.name,
        actionType: data.type,
        enabled: true,
        actionSettings: undefined,

        actionConditions: undefined,
        drawData: undefined,
        eventsFlowId: undefined,
        id: undefined,
        parentId: undefined,
        uniqueKey: GuidExtensions.generate(),
      };

      this.eventsFlowDataFormService.addEventFlowActionItemForm({ id: value.uniqueKey, actionType: value.actionType });
    }

    const newNode = {
      key: GuidExtensions.generate(),
      value: value,
      color: data.color,
      name: data.name,
      description: data.description,
      position: event.rect,
      icon: data.icon,
      input: data.hasInput ? GuidExtensions.generate() : undefined,
      output: data.hasOutput ? GuidExtensions.generate() : undefined,
      isExpandable: data.isExpandable,
      type: data.type,
      isExpanded: false,
      inputs: [],
      outputs: [GuidExtensions.generate()],
    };

    this.viewModel!.nodes.push(newNode);

    setTimeout(() => {
      this.alignContentVertically();

      if (!event.rect) {
        this.fFlowComponent.reset();
      }
    });

    this.mouseClickOnNode();

    return newNode;
  }

  public onNodePositionChanged(point: IPoint, node: any): void {
    console.log(point);
    node.position = point;
  }

  public onCreateConnection(event: FCreateConnectionEvent): void {
    console.log('onCreateConnection', event);
    if (!event.fInputId) {
      return;
    }
    this.fFlowService.createConnection({
      flow: this.viewModel,
      outputKey: event.fOutputId,
      inputKey: event.fInputId,
    });

    this.changeDetectorRef.detectChanges();
  }

  public onReassignConnection(event: FReassignConnectionEvent): void {
    console.log('onReassignConnection', event);
    this.fFlowService.reassignConnection({
      flow: this.viewModel,
      outputKey: event.fOutputId,
      oldInputKey: event.oldFInputId,
      newInputKey: event.newFInputId,
    });

    this.changeDetectorRef.detectChanges();
  }

  public onRemoveConnection(outputKey: string): void {
    console.log('onRemoveConnection', outputKey);
    const connection = this.viewModel!.connections.find(x => x.from === outputKey);

    if (!connection) {
      return;
    }

    this.fFlowService.bulkRemove({
      flow: this.viewModel,
      nodeKeys: [],
      connectionKeys: [connection.key],
    });

    this.changeDetectorRef.detectChanges();
  }

  public onSelectionChange({ fNodeIds }: FSelectionChangeEvent) {
    const matchedNodes = this.viewModel.nodes.filter(node => fNodeIds.includes(node.key));
    this.selectedNodes$.next([...matchedNodes]);
  }

  public onValueChanged(node: any, value: any): void {
    console.log('onValueChanged', node, value);
    const selected = this.fFlowComponent.getSelection();
    node.value = value;

    this.changeDetectorRef.detectChanges();
    setTimeout(() => {
      this.fFlowComponent.select(selected.fNodeIds, selected.fConnectionIds);
    });
  }

  public onActionPanelEvent(event: EFlowActionPanelEvent): void {
    switch (event) {
      case EFlowActionPanelEvent.TOGGLE_CONTENT_VIEW:
        this.isContentMinimized = !this.isContentMinimized;
        break;
      case EFlowActionPanelEvent.TEST_CALL:
        //todo this.injector.get(TestCallHandler).handle(new TestCallRequest(this.viewModel!));
        break;
      case EFlowActionPanelEvent.DELETE_SELECTED:
        this.onRemoveItems();
        break;
      case EFlowActionPanelEvent.SELECT_ALL:
        this.fFlowComponent.selectAll();
        const selection = this.fFlowComponent.getSelection();
        this.onSelectionChange(selection);
        break;
      case EFlowActionPanelEvent.ZOOM_IN:
        this.fZoomDirective.zoomIn();
        break;
      case EFlowActionPanelEvent.ZOOM_OUT:
        this.fZoomDirective.zoomOut();
        break;
      case EFlowActionPanelEvent.FIT_TO_SCREEN:
        this.fCanvasComponent.fitToScreen();
        break;
      case EFlowActionPanelEvent.ONE_TO_ONE:
        this.fCanvasComponent.resetScaleAndCenter();
        break;
      case EFlowActionPanelEvent.ALIGN_CONTENT_VERTICALLY:
        this.alignContentVertically();
        break;
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
      return;
    }
    switch (event.keyCode) {
      case BACKSPACE:
      case DELETE:
        this.onRemoveItems();
        break;
      case NUMPAD_PLUS:
        if (MouseEventExtensions.isCommandButton(EOperationSystem.WINDOWS, event)) {
          this.fZoomDirective.zoomIn();
        }
        break;
      case NUMPAD_MINUS:
      case DASH:
        if (MouseEventExtensions.isCommandButton(EOperationSystem.WINDOWS, event)) {
          this.fZoomDirective.zoomOut();
        }
        break;
      case A:
        if (MouseEventExtensions.isCommandButton(EOperationSystem.WINDOWS, event)) {
          this.fFlowComponent.selectAll();
        }
        break;
    }
  }

  public onParameterWindowClosed(): void {
    this.fFlowComponent.clearSelection();
    this.selectedNodes$.next([]);
  }

  public onNodeUpdated(updatedNode: INodeViewModel<any>): void {
    const { fNodeIds, fConnectionIds, fGroupIds } = this.fFlowComponent.getSelection();

    this.eventsFlowDataFormService.resetEventFlowEventMainData();
    this.viewModel.nodes = this.viewModel.nodes.map(node => (node.key === updatedNode.key ? updatedNode : node));

    setTimeout(() => {
      this.fFlowComponent.select(fNodeIds, fConnectionIds);
    });

    this.onSelectionChange({ fNodeIds, fConnectionIds, fGroupIds });
  }

  // NOTE: workaround for dynamic components rendering issues
  public mouseClickOnNode(): void {
    setTimeout(() => {
      const lastNodeElement = this.workflowNodes.last?.nodeHead?.nativeElement;

      const doubleClickEvent = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
      });
      lastNodeElement?.dispatchEvent(doubleClickEvent);
    }, 0);
  }

  public toggleEvents(): void {
    this.eventsOpened = !this.eventsOpened;
  }

  public toggleActionOverlay(event: MouseEvent, node: INodeViewModel<any>): void {
    if (this.actionsOpened) {
      this.closeActionOverlay();
    } else {
      //NOTE: Clear selection and close parameters sidebar before overlay opens
      this.onParameterWindowClosed();
      this.openActionOverlay(event, node);
    }
  }

  private alignContentVertically(): void {
    this.viewModel.nodes = this.eventsFlowAlignmentService.getAlignedNodes(DirectionType.TOP_TO_BOTTOM, this.viewModel);
  }

  private openActionOverlay(event: MouseEvent, node: INodeViewModel<any>): void {
    const positionStrategy = this.getPositionStrategy(event);

    const overlayConfig = {
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-actions',
      positionStrategy,
    };

    // Create the overlay
    this.overlayRef = this.overlay.create(overlayConfig);

    const componentPortal = new ComponentPortal(WorkflowPaletteActionsComponent, null, this.injector);
    const componentRef = this.overlayRef.attach(componentPortal);

    componentRef.instance.nodeType = node.type;

    componentRef.instance.actionClicked.pipe(take(1)).subscribe(data => {
      const newNode = this.onCreateNode({ data, rect: { x: -999, y: -999 } as IRect });

      this.onCreateConnection({ fOutputId: node.outputs[0], fInputId: newNode.input, fDropPosition: null });
      this.closeActionOverlay();
    });

    this.overlayRef
      .backdropClick()
      .pipe(take(1))
      .subscribe(() => {
        this.closeActionOverlay();
      });

    this.actionsOpened = true;
  }

  private closeActionOverlay(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.actionsOpened = false;
    }
  }

  private getPositionStrategy(event: MouseEvent): PositionStrategy {
    const targetElement = event.target as HTMLElement;
    const parentElement = targetElement?.parentElement?.parentElement;

    if (!parentElement) {
      throw new Error('Parent element not found');
    }

    const offset = 30;

    return this.overlay
      .position()
      .flexibleConnectedTo(parentElement)
      .withPositions([
        {
          originX: 'end',
          originY: 'center',
          overlayX: 'start',
          overlayY: 'center',
          offsetX: offset,
        },
      ])
      .withPush(true);
  }

  private convertToFlowViewModel(editModel: EventsFlowModel): IFlowViewModel<any> {
    const eventFlowViewModel: IFlowViewModel<any> = {
      key: editModel.id,
      nodes: [],
      connections: [],
    };

    const eventFlowNode: INodeViewModel<EventsFlowModel> = {
      name: editModel.drawData.name,
      description: editModel.drawData.description,
      color: editModel.drawData.color,
      icon: editModel.drawData.icon,
      isExpandable: editModel.drawData.isExpandable,
      type: editModel.drawData.type,
      key: editModel.drawData.key,
      position: editModel.drawData.position,
      input: editModel.drawData.input,
      output: editModel.drawData.output,
      inputs: editModel.drawData.inputs,
      outputs: editModel.drawData.outputs,
      isExpanded: false,
      value: {
        description: editModel.description,
        domain: editModel.domain,
        drawData: editModel.drawData,
        enabled: editModel.enabled,
        eventConditions: editModel.eventConditions,
        eventData: editModel.eventData,
        eventType: editModel.eventType,
        lastExecutionAt: editModel.lastExecutionAt,
        monthlyCount: editModel.monthlyCount || 0,
        id: editModel.id,
        items: [],
        name: editModel.name,
      },
    };

    eventFlowViewModel.nodes.push(eventFlowNode);

    for (const item of editModel.items) {
      const itemNode: INodeViewModel<EventsFlowItemModel> = {
        name: item.drawData.name,
        description: item.drawData.description,
        color: item.drawData.color,
        icon: item.drawData.icon,
        isExpandable: item.drawData.isExpandable,
        type: item.drawData.type,
        key: item.drawData.key,
        position: item.drawData.position,
        input: item.drawData.input,
        output: item.drawData.output,
        inputs: item.drawData.inputs,
        outputs: item.drawData.outputs,
        isExpanded: false,
        value: {
          actionName: item.actionName,
          actionType: item.actionType,
          enabled: item.enabled,
          actionConditions: item.actionConditions,
          actionSettings: item.actionSettings,
          drawData: item.drawData,
          eventsFlowId: item.eventsFlowId,
          id: item.id,
          monthlyFailCount: item.monthlyFailCount || 0,
          monthlySuccessCount: item.monthlySuccessCount || 0,
          lastErrorMessage: item.lastErrorMessage,
          lastRequestStatusCode: item.lastRequestStatusCode,
          lastExecutionAt: item.lastExecutionAt,
          parentId: item.parentId,
          uniqueKey: item.id || GuidExtensions.generate(),
        },
      };

      eventFlowViewModel.nodes.push(itemNode);

      eventFlowViewModel.connections = this.fFlowService.removeDisconnectedConnections(
        eventFlowViewModel.nodes,
        editModel.drawData.connections,
      );
    }

    return eventFlowViewModel;
  }

  private prepareFormModel(): EventsFlowModel {
    const eventFlowViewModel = this.fFlowService.findRootNode(this.viewModel);
    const eventFlowModel = eventFlowViewModel?.value as EventsFlowModel;

    const { eventConditions, eventFlowName } = this.eventsFlowDataFormService.rawFormValue;

    eventFlowModel.name = eventFlowName || eventFlowViewModel?.name;
    eventFlowModel.description = eventFlowViewModel?.description;
    eventFlowModel.eventType = eventFlowViewModel?.type;
    eventFlowModel.eventConditions = eventConditions;

    eventFlowModel.drawData = {
      name: eventFlowViewModel.name,
      description: eventFlowViewModel.description,
      color: eventFlowViewModel.color,
      icon: eventFlowViewModel.icon,
      input: eventFlowViewModel.input,
      output: eventFlowViewModel.output,
      inputs: eventFlowViewModel.inputs,
      type: eventFlowViewModel.type,
      key: eventFlowViewModel.key,
      isExpandable: eventFlowViewModel.isExpandable,
      isExpanded: false,
      outputs: eventFlowViewModel.outputs,
      position: eventFlowViewModel.position,
      connections: this.viewModel.connections.map(connection => {
        return {
          key: connection.key,
          from: connection.from,
          to: connection.to,
          name: connection.name,
        };
      }),
    };

    const childNodes = this.fFlowService.getAllConnectedChildNodes(eventFlowViewModel?.key, this.viewModel);

    eventFlowModel.items = [];
    for (const childNode of childNodes) {
      const childNodeValue = childNode.value as EventsFlowItemModel;

      childNodeValue.actionSettings = this.eventsFlowDataFormService.getEventFlowActionItemSettings(
        childNodeValue.uniqueKey,
      );
      childNodeValue.drawData = {
        name: childNode.name,
        description: childNode.description,
        color: childNode.color,
        icon: childNode.icon,
        input: childNode.input,
        output: childNode.output,
        inputs: childNode.inputs,
        type: childNode.type,
        key: childNode.key,
        isExpandable: childNode.isExpandable,
        isExpanded: false,
        outputs: childNode.outputs,
        position: childNode.position,
      };

      const parent = this.fFlowService.findParentNode(childNode.key, this.viewModel);
      if (parent.key !== eventFlowViewModel?.key) {
        childNodeValue.parent = parent?.value as EventsFlowItemModel;
        childNodeValue.parentId = (parent?.value as EventsFlowItemModel)?.parentId;
        childNodeValue.parentUniqueKey = (parent?.value as EventsFlowItemModel)?.parentUniqueKey;
      }
      eventFlowModel.items.push(childNodeValue);
    }

    return eventFlowModel;
  }

  private subscribeReloadEvents(): Subscription {
    return merge(this.hasChanges$, this.routeKeyChange$).subscribe(async res => {
      const id = this.activatedRoute.snapshot.params['id'];
      if (id !== 'new') {
        try {
          this.editModel = await this.eventsFlowService.getEventsFlowById(id);

          this.viewModel = this.convertToFlowViewModel(this.editModel);
        } catch (e) {
          console.error(e);
          this.editModel = undefined;
          this.viewModel = undefined;
        }
      }

      this.eventsFlowDataFormService.initializeForm(this.editModel);
      this.isDataLoaded$.next(true);
    });
  }

  private async processSubmit(): Promise<void> {
    const reqData = this.prepareFormModel();

    try {
      await this.eventsFlowService.saveEventsFlow(reqData);
      this.notificationService.notifySuccess('Events Flow updated successfully');
      this.headerService.goToComponent('EventsFlowListComponent');
    } catch (e) {
      this.serverError = true;
    } finally {
      this.submitted = false;
    }
  }

  private async onDeleteProcess(): Promise<void> {
    await this.eventsFlowService.deleteEventFlow(this.editModel.id);

    this.notificationService.notifySuccess('Events Flow deleted successfully');
    this.headerService.goToComponent('EventsFlowListComponent');
  }

  private onRemoveItems(): void {
    const selection = this.fFlowComponent.getSelection();

    this.fFlowService.bulkRemove({
      flow: this.viewModel,
      nodeKeys: selection.fNodeIds,
      connectionKeys: selection.fConnectionIds,
    });

    this.selectedNodes$.next([]);
    this.changeDetectorRef.detectChanges();
  }

  private loadNotes(): void {
    forkJoin({
      caregiverNotes: this.eventsFlowService.getCaregiverNoteSubject().pipe(
        catchError(err => {
          console.error('Failed to load Caregiver Notes', err);
          return of([]);
        }),
      ),
      patientNotes: this.eventsFlowService.getPatientNoteReasons().pipe(
        catchError(err => {
          console.error('Failed to load Patient Notes', err);
          return of([]);
        }),
      ),
    }).subscribe({
      next: ({ caregiverNotes, patientNotes }) => {
        this.actionNotes$.next({
          caregiverNotes: caregiverNotes.map(n => ({ ...n, id: String(n.id) })),
          patientNotes: patientNotes.map(n => ({ ...n, id: String(n.id) })),
        });
      },
    });
  }
}
